import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { itemAPI } from '../services/api';
import { useAuth } from '../contexts/FirebaseAuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import PromoSlot from '../components/common/PromoSlot';
import StarRating from '../components/common/StarRating';
import ReportItemModal from '../components/common/ReportItemModal';
import { Heart, MapPin, ChevronLeft, ChevronRight, MessageCircle, Share2, Bookmark, Edit2, Trash2, Lock, ShieldCheck, Flag } from 'lucide-react';

const ItemDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showReportModal, setShowReportModal] = useState(false);

  // Handle responsive layout
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get current image URL
  const getCurrentImage = () => {
    if (!item || !item.images || item.images.length === 0) {
      return 'https://via.placeholder.com/800x600?text=No+Image';
    }

    const currentImg = item.images[currentImageIndex];

    if (typeof currentImg === 'object' && currentImg !== null) {
      return currentImg.fullSize || currentImg.thumbnail || currentImg.url || 'https://via.placeholder.com/800x600?text=No+Image';
    }
    if (typeof currentImg === 'string') {
      return currentImg;
    }

    return 'https://via.placeholder.com/800x600?text=No+Image';
  };

  // Fetch item details
  useEffect(() => {
    const fetchItemDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await itemAPI.getItem(id);

        if (res.data.success && res.data.data) {
          const itemData = res.data.data;

          // Normalize the item data
          setItem({
            ...itemData,
            _id: itemData._id || itemData.id,
            id: itemData._id || itemData.id,
          });

          // Check if user has liked/saved this item
          if (isAuthenticated && user) {
            setLiked(itemData.likes?.includes(user._id) || false);
            setSaved(user.savedItems?.includes(itemData._id) || false);
          }

          // Set like count
          setLikeCount(itemData.likes?.length || 0);

          console.log('✅ Item loaded:', itemData.title);
        } else {
          throw new Error('Item not found');
        }
      } catch (err) {
        console.error('Failed to load item:', err);
        setError(err.response?.data?.message || 'Item not found');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchItemDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Handle like/unlike
  const handleLikeToggle = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      if (liked) {
        await itemAPI.unlikeItem(item._id);
        setLiked(false);
        setLikeCount((prev) => prev - 1);
      } else {
        await itemAPI.likeItem(item._id);
        setLiked(true);
        setLikeCount((prev) => prev + 1);
      }
    } catch (err) {
      console.error('Failed to toggle like:', err);
    }
  };

  // Handle save/unsave
  const handleSaveToggle = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      if (saved) {
        await itemAPI.unsaveItem(item._id);
        setSaved(false);
      } else {
        await itemAPI.saveItem(item._id);
        setSaved(true);
      }
    } catch (err) {
      console.error('Failed to toggle save:', err);
    }
  };

  // Handle delete item
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this item? This action cannot be undone.')) {
      return;
    }

    try {
      await itemAPI.deleteItem(item._id);
      navigate('/seller-dashboard');
    } catch (err) {
      console.error('Failed to delete item:', err);
      alert('Failed to delete item. Please try again.');
    }
  };

  // Handle edit item
  const handleEdit = () => {
    navigate(`/edit-listing/${item._id}`);
  };

  // Navigate images
  const nextImage = () => {
    if (item && item.images && item.images.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % item.images.length);
    }
  };

  const prevImage = () => {
    if (item && item.images && item.images.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + item.images.length) % item.images.length);
    }
  };

  // Handle contact seller
  const handleContactSeller = () => {
    if (item?.user?._id) {
      // Pass the item context so the seller knows which listing is being discussed
      navigate(`/messages/${item.user._id}`, {
        state: {
          item: {
            _id: item._id,
            title: item.title,
            price: item.price,
            images: item.images,
          },
        },
      });
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !item) {
    return (
      <div className="container" style={{ paddingTop: '60px', textAlign: 'center' }}>
        <div className="card" style={{ maxWidth: '500px', margin: '0 auto', padding: '40px' }}>
          <h2 style={{ marginBottom: '16px', color: 'var(--danger)' }}>Item Not Found</h2>
          <p className="text-muted" style={{ marginBottom: '24px' }}>
            {error || 'The item you are looking for does not exist or has been removed.'}
          </p>
          <button onClick={() => navigate('/')} className="btn btn-primary">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: '40px', paddingBottom: '60px', maxWidth: '100%', overflowX: 'hidden' }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: isMobile ? '20px' : '40px',
        marginBottom: '40px'
      }}>
        {/* Image Gallery */}
        <div style={{ width: '100%', maxWidth: isMobile ? '100%' : '600px' }}>
          <div
            style={{
              position: 'relative',
              width: '100%',
              paddingBottom: '100%',
              backgroundColor: 'var(--secondary)',
              borderRadius: '12px',
              overflow: 'hidden',
              marginBottom: '16px',
            }}
          >
            <img
              src={getCurrentImage()}
              alt={item.title}
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/800x600?text=Image+Error';
              }}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />

            {item.images && item.images.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <ChevronLeft size={24} />
                </button>

                <button
                  onClick={nextImage}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <ChevronRight size={24} />
                </button>

                <div
                  style={{
                    position: 'absolute',
                    bottom: '12px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '16px',
                    fontSize: '14px',
                  }}
                >
                  {currentImageIndex + 1} / {item.images.length}
                </div>
              </>
            )}
          </div>

          {/* Thumbnail Strip */}
          {item.images && item.images.length > 1 && (
            <div style={{ display: 'flex', gap: '12px', overflowX: 'auto' }}>
              {item.images.map((img, index) => {
                const thumbUrl =
                  typeof img === 'object'
                    ? img.thumbnail || img.fullSize || img.url
                    : img;

                return (
                  <div
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    style={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border:
                        currentImageIndex === index
                          ? '3px solid var(--primary)'
                          : '2px solid var(--border)',
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={thumbUrl || 'https://via.placeholder.com/80'}
                      alt={`Thumbnail ${index + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Item Details */}
        <div>
          {/* Title and Action Buttons */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
            <h1 style={{ fontSize: isMobile ? '24px' : '32px', marginBottom: '8px', flex: isMobile ? '1 1 100%' : '1' }}>{item.title}</h1>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {/* Like Button */}
              <button
                onClick={handleLikeToggle}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
                title="Like this item"
              >
                <Heart
                  size={28}
                  fill={liked ? '#e60023' : 'none'}
                  color={liked ? '#e60023' : '#666'}
                />
                {likeCount > 0 && (
                  <span style={{ fontSize: '14px', color: '#666', fontWeight: '600' }}>
                    {likeCount}
                  </span>
                )}
              </button>

              {/* Save/Bookmark Button */}
              <button
                onClick={handleSaveToggle}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                }}
                title="Save for later"
              >
                <Bookmark
                  size={28}
                  fill={saved ? '#2196F3' : 'none'}
                  color={saved ? '#2196F3' : '#666'}
                />
              </button>
            </div>
          </div>

          {/* Price */}
          <div style={{ marginBottom: '24px' }}>
            <span style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: '700', color: 'var(--primary)' }}>
              ${item.price?.toFixed(2)}
            </span>
            {item.originalPrice && item.originalPrice > item.price && (
              <span
                style={{
                  fontSize: '24px',
                  color: 'var(--textSecondary)',
                  textDecoration: 'line-through',
                  marginLeft: '12px',
                }}
              >
                ${item.originalPrice.toFixed(2)}
              </span>
            )}
          </div>

          {/* Condition Badge */}
          {item.condition && (
            <div style={{ marginBottom: '24px' }}>
              <span
                style={{
                  display: 'inline-block',
                  background: 'var(--primary)',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  fontSize: '14px',
                  fontWeight: '600',
                }}
              >
                {item.condition}
              </span>
            </div>
          )}

          {/* Item Info */}
          <div style={{ marginBottom: '24px' }}>
            {item.category && (
              <div style={{ marginBottom: '12px' }}>
                <strong style={{ color: 'var(--textSecondary)', fontSize: '14px' }}>Category:</strong>{' '}
                <span>{item.category}</span>
              </div>
            )}
            {item.ageGroup && (
              <div style={{ marginBottom: '12px' }}>
                <strong style={{ color: 'var(--textSecondary)', fontSize: '14px' }}>Age Group:</strong>{' '}
                <span>{item.ageGroup}</span>
              </div>
            )}
            {item.brand && (
              <div style={{ marginBottom: '12px' }}>
                <strong style={{ color: 'var(--textSecondary)', fontSize: '14px' }}>Brand:</strong>{' '}
                <span>{item.brand}</span>
              </div>
            )}
            {item.color && (
              <div style={{ marginBottom: '12px' }}>
                <strong style={{ color: 'var(--textSecondary)', fontSize: '14px' }}>Color:</strong>{' '}
                <span>{item.color}</span>
              </div>
            )}
            {item.location && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
                <MapPin size={16} color="var(--textSecondary)" />
                <span>{item.location}</span>
              </div>
            )}
          </div>

          {/* Description */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '18px', marginBottom: '12px' }}>Description</h3>
            <p style={{ lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{item.description}</p>
          </div>

          {/* Inline promo below description */}
          <div style={{ marginBottom: '24px' }}>
            <PromoSlot slotId="product_detail_below_desc" variant="inline" />
          </div>

          {/* Tags */}
          {item.tags && item.tags.length > 0 && (
            <div style={{ marginBottom: '32px' }}>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {item.tags.map((tag) => (
                  <span
                    key={tag}
                    style={{
                      background: 'var(--secondary)',
                      padding: '6px 12px',
                      borderRadius: '16px',
                      fontSize: '13px',
                      color: 'var(--textSecondary)',
                    }}
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Seller Info */}
          {item.user && (
            <div
              style={{
                background: 'var(--secondary)',
                padding: '20px',
                borderRadius: '12px',
                marginBottom: '24px',
              }}
            >
              <h3 style={{ fontSize: '16px', marginBottom: '12px' }}>Seller</h3>
              <Link
                to={`/profile/${item.user.username || item.user._id}`}
                style={{ display: 'flex', alignItems: 'center', gap: '12px', textDecoration: 'none' }}
              >
                <img
                  src={
                    item.user.profileImage ||
                    `https://ui-avatars.com/api/?name=${encodeURIComponent(item.user.username || 'User')}&size=64`
                  }
                  alt={item.user.username}
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', fontSize: '16px' }}>{item.user.username}</div>
                  {item.user.averageRating > 0 && (
                    <div style={{ marginTop: '4px' }}>
                      <StarRating
                        rating={item.user.averageRating}
                        totalRatings={item.user.totalRatings}
                        size={14}
                      />
                    </div>
                  )}
                  {item.user.averageRating === 0 && (
                    <div style={{ fontSize: '13px', color: 'var(--textSecondary)', marginTop: '4px' }}>
                      No ratings yet
                    </div>
                  )}
                </div>
              </Link>
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Owner Actions - Only show if user owns this item */}
            {isAuthenticated && user && item.user && user._id === item.user._id && (
              <div style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>
                <button
                  onClick={handleEdit}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '14px',
                    background: '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  <Edit2 size={20} />
                  Edit Listing
                </button>
                <button
                  onClick={handleDelete}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '14px',
                    background: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  <Trash2 size={20} />
                  Delete
                </button>
              </div>
            )}

            {/* Buy Now Button - Only show if item is active and user doesn't own it */}
            {item.status === 'active' && !(isAuthenticated && user && item.user && user._id === item.user._id) && (
              <>
                <button
                  onClick={() => navigate(`/checkout/${item._id}`)}
                  className="btn btn-primary"
                  style={{
                    width: '100%',
                    padding: '16px',
                    fontSize: '18px',
                    fontWeight: '700',
                  }}
                >
                  Buy Now - ${item.price}
                </button>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    padding: '10px 4px',
                    fontSize: '13px',
                    color: 'var(--textSecondary)',
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <Lock size={14} color="#0a7a5b" />
                    Payments processed securely by Stripe
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <ShieldCheck size={14} color="#0a7a5b" />
                    Your payment is held in escrow until you confirm delivery
                  </span>
                </div>
              </>
            )}

            {/* Secondary Actions */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleContactSeller}
                className="btn"
                style={{
                  flex: 1,
                  padding: '14px',
                  fontSize: '16px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                <MessageCircle size={20} />
                Contact Seller
              </button>

              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  alert('Link copied to clipboard!');
                }}
                className="btn"
                style={{
                  padding: '14px 20px',
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Share2 size={20} />
              </button>
            </div>

            {/* Report listing — hidden for the owner */}
            {item.user?._id !== user?._id && (
              <button
                onClick={() => {
                  if (!isAuthenticated) {
                    navigate('/login');
                    return;
                  }
                  setShowReportModal(true);
                }}
                style={{
                  marginTop: '16px',
                  background: 'none',
                  border: 'none',
                  color: 'var(--textSecondary)',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  padding: 0,
                }}
                title="Report this listing"
              >
                <Flag size={14} />
                Report this listing
              </button>
            )}
          </div>
        </div>
      </div>

      {showReportModal && (
        <ReportItemModal
          item={item}
          onClose={() => setShowReportModal(false)}
        />
      )}
    </div>
  );
};

export default ItemDetail;
